import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { X, Plus, CalendarIcon, AlertCircle } from "lucide-react";
import { FilterRow, FilterLogic, FilterOperator, FilterField } from "@/types/filters";
import {
  getFieldType,
  getOperatorsForFieldType,
  operatorRequiresValue,
  operatorRequiresTwoValues,
  generateFilterSummary,
  validateFilterRow,
  getFieldsForEntity,
} from "@/lib/filter-utils";
import { cn } from "@/lib/utils";

interface AdvancedFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyFilters: (rows: FilterRow[], logic: FilterLogic[]) => void;
  initialFilters?: FilterRow[];
  initialLogic?: FilterLogic[];
  entityType?: string;
  customFields?: FilterField[];
}

const MAX_FILTERS = 10;

export function AdvancedFiltersModal({
  open,
  onOpenChange,
  onApplyFilters,
  initialFilters = [],
  initialLogic = [],
  entityType,
  customFields,
}: AdvancedFiltersModalProps) {
  const [filterRows, setFilterRows] = useState<FilterRow[]>([]);
  const [filterLogic, setFilterLogic] = useState<FilterLogic[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get fields for the specific entity type, or use custom fields if provided
  const availableFields = useMemo(() => {
    if (customFields && customFields.length > 0) {
      return customFields;
    }
    return getFieldsForEntity(entityType);
  }, [entityType, customFields]);

  // Initialize with existing filters or create default row
  useEffect(() => {
    if (open) {
      if (initialFilters.length > 0) {
        setFilterRows(initialFilters);
        setFilterLogic(initialLogic);
      } else {
        setFilterRows([createDefaultFilterRow()]);
        setFilterLogic([]);
      }
      setErrors({});
    }
  }, [open, initialFilters, initialLogic]);

  function createDefaultFilterRow(): FilterRow {
    // Use the first available field for the entity type, fallback to "code" for backwards compatibility
    const defaultField = availableFields.length > 0 ? availableFields[0].id : "code";
    return {
      id: `filter-${Date.now()}-${Math.random()}`,
      field: defaultField,
      operator: "equals",
      value: null,
      value2: null,
    };
  }

  function handleAddFilter() {
    if (filterRows.length >= MAX_FILTERS) {
      return;
    }

    const newRow = createDefaultFilterRow();
    setFilterRows([...filterRows, newRow]);
    
    // Add default logic for the previous row
    if (filterRows.length > 0) {
      setFilterLogic([...filterLogic, "AND"]);
    }
  }

  function handleRemoveFilter(id: string) {
    if (filterRows.length === 1) return;

    const index = filterRows.findIndex((row) => row.id === id);
    const newRows = filterRows.filter((row) => row.id !== id);
    setFilterRows(newRows);

    // Remove corresponding logic
    const newLogic = [...filterLogic];
    if (index > 0) {
      newLogic.splice(index - 1, 1);
    } else if (newLogic.length > 0) {
      newLogic.splice(0, 1);
    }
    setFilterLogic(newLogic);

    // Remove error for this row
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
  }

  function handleFieldChange(id: string, field: string) {
    const newRows = filterRows.map((row) => {
      if (row.id === id) {
        const fieldType = getFieldType(field, availableFields);
        const operators = fieldType ? getOperatorsForFieldType(fieldType) : [];
        const defaultOperator = operators[0]?.value || "equals";

        return {
          ...row,
          field,
          operator: defaultOperator as FilterOperator,
          value: null,
          value2: null,
        };
      }
      return row;
    });
    setFilterRows(newRows);

    // Clear error for this row
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
  }

  function handleOperatorChange(id: string, operator: FilterOperator) {
    const newRows = filterRows.map((row) => {
      if (row.id === id) {
        return {
          ...row,
          operator,
          value: null,
          value2: null,
        };
      }
      return row;
    });
    setFilterRows(newRows);

    // Clear error for this row
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
  }

  function handleValueChange(id: string, value: string | number | Date | null) {
    const newRows = filterRows.map((row) => {
      if (row.id === id) {
        return { ...row, value };
      }
      return row;
    });
    setFilterRows(newRows);

    // Clear error for this row
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
  }

  function handleValue2Change(id: string, value2: string | number | Date | null) {
    const newRows = filterRows.map((row) => {
      if (row.id === id) {
        return { ...row, value2 };
      }
      return row;
    });
    setFilterRows(newRows);

    // Clear error for this row
    const newErrors = { ...errors };
    delete newErrors[id];
    setErrors(newErrors);
  }

  function handleLogicChange(index: number, logic: FilterLogic) {
    const newLogic = [...filterLogic];
    newLogic[index] = logic;
    setFilterLogic(newLogic);
  }

  function handleClearAll() {
    // Clear the modal state
    setFilterRows([createDefaultFilterRow()]);
    setFilterLogic([]);
    setErrors({});
    // Also apply the cleared filters to remove any active filters
    onApplyFilters([], []);
    onOpenChange(false);
  }

  function handleApply() {
    // Validate all rows
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    filterRows.forEach((row) => {
      const error = validateFilterRow(row);
      if (error) {
        newErrors[row.id] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    // Apply filters
    onApplyFilters(filterRows, filterLogic);
    onOpenChange(false);
  }

  const filterSummary = useMemo(() => {
    return generateFilterSummary(filterRows, filterLogic);
  }, [filterRows, filterLogic]);

  const canAddMore = filterRows.length < MAX_FILTERS;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Advanced Filters</DialogTitle>
          <DialogDescription>
            Create custom filters to find specific stock items
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Add Filter Button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddFilter}
              disabled={!canAddMore}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Filter
            </Button>
            {!canAddMore && (
              <span className="text-sm text-muted-foreground">
                Maximum {MAX_FILTERS} filters reached
              </span>
            )}
          </div>

          {/* Filter Rows */}
          <div className="space-y-3">
            {filterRows.map((row, index) => (
              <div key={row.id} className="space-y-3">
                  <FilterRowComponent
                    row={row}
                    error={errors[row.id]}
                    canRemove={filterRows.length > 1}
                    availableFields={availableFields}
                    onFieldChange={(field) => handleFieldChange(row.id, field)}
                    onOperatorChange={(operator) => handleOperatorChange(row.id, operator)}
                    onValueChange={(value) => handleValueChange(row.id, value)}
                    onValue2Change={(value2) => handleValue2Change(row.id, value2)}
                    onRemove={() => handleRemoveFilter(row.id)}
                  />

                {/* Logic Selector (between rows) */}
                {index < filterRows.length - 1 && (
                  <div className="flex justify-center">
                    <ToggleGroup
                      type="single"
                      value={filterLogic[index] || "AND"}
                      onValueChange={(value) => {
                        if (value) handleLogicChange(index, value as FilterLogic);
                      }}
                      variant="outline"
                    >
                      <ToggleGroupItem value="AND" className="min-w-[80px]">
                        AND
                      </ToggleGroupItem>
                      <ToggleGroupItem value="OR" className="min-w-[80px]">
                        OR
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Filter Summary */}
          {filterSummary && (
            <div className="pt-4 border-t">
              <p className="text-sm text-muted-foreground italic">
                {filterSummary}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="outline" onClick={handleClearAll}>
            Clear All
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={hasErrors}>
              Apply Filters
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface FilterRowComponentProps {
  row: FilterRow;
  error?: string;
  canRemove: boolean;
  availableFields: FilterField[];
  onFieldChange: (field: string) => void;
  onOperatorChange: (operator: FilterOperator) => void;
  onValueChange: (value: string | number | Date | null) => void;
  onValue2Change: (value2: string | number | Date | null) => void;
  onRemove: () => void;
}

function FilterRowComponent({
  row,
  error,
  canRemove,
  availableFields,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onValue2Change,
  onRemove,
}: FilterRowComponentProps) {
  const fieldType = getFieldType(row.field, availableFields);
  const operators = fieldType ? getOperatorsForFieldType(fieldType) : [];
  const requiresValue = operatorRequiresValue(row.operator);

  // Convert fields to combobox options - use entity-specific fields
  const fieldOptions: ComboboxOption[] = availableFields.map((field) => ({
    value: field.id,
    label: field.label,
  }));

  // Convert operators to combobox options
  const operatorOptions: ComboboxOption[] = operators.map((op) => ({
    value: op.value,
    label: op.label,
  }));

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[200px_150px_1fr_40px] gap-2 items-start">
        {/* Field Selector */}
        <div>
          <Combobox
            options={fieldOptions}
            value={row.field}
            onValueChange={onFieldChange}
            placeholder="Select field..."
            searchPlaceholder="Search fields..."
            emptyText="No fields found."
            className={cn(error && "border-destructive")}
          />
        </div>

        {/* Operator Selector */}
        <div>
          <Combobox
            options={operatorOptions}
            value={row.operator}
            onValueChange={(value) => onOperatorChange(value as FilterOperator)}
            placeholder="Select operator..."
            searchPlaceholder="Search operators..."
            emptyText="No operators found."
            className={cn(error && "border-destructive")}
          />
        </div>

        {/* Value Input(s) */}
        <div>
          {requiresValue && (
            <ValueInput
              fieldType={fieldType}
              operator={row.operator}
              field={row.field}
              value={row.value}
              value2={row.value2}
              onValueChange={onValueChange}
              onValue2Change={onValue2Change}
              hasError={!!error}
              availableFields={availableFields}
            />
          )}
        </div>

        {/* Remove Button */}
        <div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={!canRemove}
            className="h-10 w-10"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

interface ValueInputProps {
  fieldType: string | null;
  operator: FilterOperator;
  field: string;
  value: string | number | Date | null;
  value2?: string | number | Date | null;
  onValueChange: (value: string | number | Date | null) => void;
  onValue2Change: (value2: string | number | Date | null) => void;
  hasError: boolean;
  availableFields: FilterField[];
}

function ValueInput({
  fieldType,
  operator,
  field,
  value,
  value2,
  onValueChange,
  onValue2Change,
  hasError,
  availableFields,
}: ValueInputProps) {
  const requiresTwoValues = operatorRequiresTwoValues(operator);

  if (fieldType === "text") {
    return (
      <Input
        type="text"
        value={value as string || ""}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder="Enter value"
        className={cn(hasError && "border-destructive")}
      />
    );
  }

  if (fieldType === "select") {
    // Get the field definition to access options - use entity-specific fields
    const fieldDef = availableFields.find((f) => f.id === field);
    const selectOptions: ComboboxOption[] = fieldDef?.options?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) || [];

    return (
      <Combobox
        options={selectOptions}
        value={value as string || ""}
        onValueChange={onValueChange}
        placeholder="Select value..."
        searchPlaceholder="Search..."
        emptyText="No options found."
        className={cn(hasError && "border-destructive")}
      />
    );
  }

  if (fieldType === "number") {
    if (requiresTwoValues) {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={value as number || ""}
            onChange={(e) => onValueChange(e.target.value ? Number(e.target.value) : null)}
            placeholder="Min"
            className={cn(hasError && "border-destructive")}
          />
          <span className="text-sm text-muted-foreground">and</span>
          <Input
            type="number"
            value={value2 as number || ""}
            onChange={(e) => onValue2Change(e.target.value ? Number(e.target.value) : null)}
            placeholder="Max"
            className={cn(hasError && "border-destructive")}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value as number || ""}
          onChange={(e) => onValueChange(e.target.value ? Number(e.target.value) : null)}
          placeholder="Enter value"
          className={cn(hasError && "border-destructive")}
        />
        {operator === "inLastXDays" && (
          <span className="text-sm text-muted-foreground whitespace-nowrap">days</span>
        )}
      </div>
    );
  }

  if (fieldType === "date") {
    if (operator === "inLastXDays") {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={value as number || ""}
            onChange={(e) => onValueChange(e.target.value ? Number(e.target.value) : null)}
            placeholder="Number of days"
            className={cn(hasError && "border-destructive")}
          />
          <span className="text-sm text-muted-foreground whitespace-nowrap">days</span>
        </div>
      );
    }

    if (requiresTwoValues) {
      return (
        <div className="flex items-center gap-2">
          <DatePicker
            value={value as Date}
            onChange={onValueChange}
            hasError={hasError}
            placeholder="Start date"
          />
          <span className="text-sm text-muted-foreground">and</span>
          <DatePicker
            value={value2 as Date}
            onChange={onValue2Change}
            hasError={hasError}
            placeholder="End date"
          />
        </div>
      );
    }

    return (
      <DatePicker
        value={value as Date}
        onChange={onValueChange}
        hasError={hasError}
      />
    );
  }

  return null;
}

interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  hasError: boolean;
  placeholder?: string;
}

function DatePicker({ value, onChange, hasError, placeholder = "Select date" }: DatePickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            hasError && "border-destructive"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? value.toLocaleDateString("en-GB") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value || undefined}
          onSelect={(date) => {
            onChange(date || null);
            setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
